import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { uploadImage } from "../../../helper/apicalls/imageUpload";
import "../../../styles/common/popup.scss";

const ImageUploadPopup = ({ open, handleClose, folderId, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageName, setImageName] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5MB");
        return;
      }

      setSelectedFile(file);
      const fileNameWithoutExt = file.name.split(".").slice(0, -1).join(".");
      setImageName(fileNameWithoutExt);
      setError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image to upload");
      return;
    }

    if (!imageName.trim()) {
      setError("Please enter a name for the image");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append("name", imageName.trim());
      formData.append("file", selectedFile);

      const response = await uploadImage(folderId, formData);

      if (response) {
        const imageUrl =
          response.data?.image_url || response.image_url || response.data?.url;
        if (imageUrl) {
          setUploadedImageUrl(imageUrl);
        }
        handleClosePopup();
        onUploadSuccess();
      } else {
        setError(response.message || "Upload failed");
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      setError(err.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(uploadedImageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
      const textArea = document.createElement("textarea");
      textArea.value = uploadedImageUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClosePopup = () => {
    if (!uploading) {
      setSelectedFile(null);
      setImageName("");
      setPreview(null);
      setError(null);
      setUploadedImageUrl(null);
      setCopied(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      handleClose();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  if (!open) return null;

  return createPortal(
    <div className="common-popup-overlay" onClick={handleClosePopup}>
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          <h2>Upload Image</h2>
          <button
            type="button"
            className="popup-close-button"
            onClick={handleClosePopup}
            disabled={uploading}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          {/* {preview && ( */}
          <div className="popup-form-group">
            <label>Image Name:</label>
            <input
              type="text"
              className="popup-input"
              value={imageName}
              onChange={(e) => setImageName(e.target.value)}
              placeholder="Enter image name"
              disabled={uploading}
            />
          </div>
          {/* )} */}
          <div className="popup-form-group">
            <div
              className="drop-area"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                disabled={uploading}
              />

              {preview ? (
                <div className="upload-ui">
                  <img
                    src={preview}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      borderRadius: "4px",
                    }}
                  />
                  <p className="upload-text">{selectedFile.name}</p>
                </div>
              ) : (
                <div className="upload-ui">
                  <p className="upload-text">
                    Click to select or <span>browse</span> to drag and drop an
                    image here
                  </p>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "var(--color-text-light)",
                      margin: "8px 0 0 0",
                    }}
                  >
                    Supported formats: JPG, PNG, GIF, WebP (Max 5MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {error && <div className="popup-error-message">{error}</div>}

          {uploading && <div className="uploading-text">Uploading...</div>}

          {uploadedImageUrl && (
            <div className="popup-note">
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "var(--color-success, #155724)",
                }}
              >
                Image uploaded successfully!
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--color-success, #155724)",
                  marginBottom: "8px",
                }}
              >
                Image URL:
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  value={uploadedImageUrl}
                  readOnly
                  className="popup-input"
                  style={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    padding: "6px 8px",
                  }}
                />
                <button
                  onClick={handleCopyUrl}
                  className={`popup-submit-button ${copied ? "success" : ""}`}
                  disabled={uploading}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="popup-actions popup-btn">
          <button
            type="button"
            className="popup-cancel-button"
            onClick={handleClosePopup}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="popup-submit-button"
            onClick={handleUpload}
            disabled={!selectedFile || !imageName.trim() || uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageUploadPopup;
